import React from "react";
import { Label } from "../../misc/Label";
import Checkbox from "../../misc/Checkbox";
import { cn } from "../../../utils/classMerger";
import { MlsPropertyTypeOption } from "../../../types/mlsPropertyTypes";

interface MlsPropertyTypesSectionProps {
    options: MlsPropertyTypeOption[];
    selected: Set<string>;
    onToggle: (token: string) => void;
    canEdit: boolean;
    loading: boolean;
}

// Presentational: which Trestle PropertyType tokens auto-sync should pull. State + persistence
// live in the parent (MlsDetails) so the toggle and this preference save together in one call.
const MlsPropertyTypesSection: React.FC<MlsPropertyTypesSectionProps> = ({
    options,
    selected,
    onToggle,
    canEdit,
    loading,
}) => {
    return (
        <div>
            <Label className="font-semibold text-primary">
                Property Types to Auto-Sync
            </Label>
            <p className="mt-2 text-sm text-gray-500">
                Only the checked property types are auto-synced from your MLS.
                Unchecked types (land, farm, and so on) are skipped and moved to
                the Excluded list, where you can still add them manually.
                Commercial types are different - when unchecked, those listings
                are not brought in at all and will not show in the Excluded
                list. You can still add one any time by its MLS number.
            </p>

            {loading ? (
                <div className="mt-4 grid grid-cols-1 gap-3 sxl:grid-cols-2">
                    {[0, 1, 2, 3].map((i) => (
                        <div
                            key={i}
                            className="h-6 rounded bg-gray-200 animate-skeleton"
                        />
                    ))}
                </div>
            ) : (
                <div
                    className={cn(
                        "mt-4 grid grid-cols-1 gap-3 sxl:grid-cols-2",
                        !canEdit && "opacity-50"
                    )}
                >
                    {options.map((option) => (
                        <Checkbox
                            key={option.token}
                            variant="radix"
                            id={`ptype-${option.token}`}
                            label={option.label}
                            checked={selected.has(option.token)}
                            onChange={() => canEdit && onToggle(option.token)}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

export default MlsPropertyTypesSection;
